// client/src/utils/paymentTerms.ts
//
// Due date from the invoice date and the payment terms.
//
// MIRRORED FILE: src/utils/paymentTerms.js is a line-for-line twin — see the
// header comment there for why this arithmetic exists at all. CRA's
// ModuleScopePlugin forbids importing from src/, so the copy is the only option.
// Change one, change both.

// Terms beyond a year are a misread of something else, not credit terms.
export const MAX_TERM_DAYS = 365;

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/;
// A numeric date, optionally followed by the clock a supplier printed beside it
// ("09:00", "10:30 AM", ", 09:00", "09:00 GMT-0400"). The clock is read past and
// dropped, as ISO_RE drops everything after its `T`: an invoice date is a
// calendar day. USC-BUG-0009 / 0012 / 0017 / 0025: a date with a clock used to
// fall through to `new Date`, which is month-first. See the server twin.
const NUMERIC_RE = /^(\d{1,4})([/\-.])(\d{1,2})\2(\d{2,4})(?:(?:\s*,\s*|\s+|T)(?:\d{1,2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:\s*[ap]\.?\s?m\.?)?|\d{1,2}\s*[ap]\.?\s?m\.?)(?:\s*(?:z|[a-z]{2,5}(?:[+-]\d{1,2}(?::?\d{2})?)?|[+-]\d{1,2}(?::?\d{2})?))?)?$/i;
// A day or a month in first place, whatever follows it: the one numeric shape
// the native parser reads the wrong way round. Year-first it never transposes.
const DAY_MONTH_LEAD_RE = /^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}(?!\d)/;
const TEXT_DMY_RE = /^(\d{1,2})(?:st|nd|rd|th)?[\s\-]+([A-Za-z]{3,9})\.?[\s,\-]+(\d{2,4})$/;
const TEXT_MDY_RE = /^([A-Za-z]{3,9})\.?[\s\-]+(\d{1,2})(?:st|nd|rd|th)?[\s,]+(\d{2,4})$/;

export type DateFormat = {
  kind: "iso" | "numeric" | "text";
  sep?: string;
  order?: "dmy" | "mdy" | "ymd";
  yearDigits?: 2 | 4;
};

/** Two-digit years: an invoice is never 90 years old, so 26 is 2026. */
function expandYear(y: string): number | null {
  const n = Number(y);
  if (!Number.isFinite(n)) return null;
  if (String(y).trim().length > 2) return n;
  return n < 70 ? 2000 + n : 1900 + n;
}

function monthIndexFromName(name: string): number | null {
  const key = String(name || "").trim().toLowerCase().slice(0, 3);
  const idx = MONTH_NAMES.indexOf(key);
  return idx === -1 ? null : idx;
}

/** UTC throughout: a date-only value must not shift a day by running in AST. */
function makeUtcDate(year: number | null, monthIndex: number, day: number): Date | null {
  if (year === null || !Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) {
    return null;
  }
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  const d = new Date(Date.UTC(year, monthIndex, day));
  // Rejects 31/02 and friends, which JS would silently roll into March.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== monthIndex || d.getUTCDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Parse whatever date shape the invoice used, remembering the shape so the
 * derived due date can be written back in the same one. Day-first is the
 * default for an ambiguous numeric date — these are Trinidad invoices.
 */
export function parseInvoiceDate(raw: unknown): { date: Date; format: DateFormat } | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;

  const iso = text.match(ISO_RE);
  if (iso) {
    const date = makeUtcDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return date ? { date, format: { kind: "iso" } } : null;
  }

  const numeric = text.match(NUMERIC_RE);
  if (numeric) {
    const [, first, sep, middle, last] = numeric;
    const a = Number(first);
    const b = Number(middle);

    if (first.length === 4) {
      const date = makeUtcDate(a, b - 1, Number(last));
      return date ? { date, format: { kind: "numeric", sep, order: "ymd", yearDigits: 4 } } : null;
    }

    const year = expandYear(last);
    const monthFirst = a <= 12 && b > 12;
    const day = monthFirst ? b : a;
    const month = monthFirst ? a : b;
    const date = makeUtcDate(year, month - 1, day);
    if (!date) return null;
    return {
      date,
      format: {
        kind: "numeric",
        sep,
        order: monthFirst ? "mdy" : "dmy",
        yearDigits: String(last).trim().length === 2 ? 2 : 4,
      },
    };
  }

  const dmy = text.match(TEXT_DMY_RE);
  if (dmy) {
    const monthIndex = monthIndexFromName(dmy[2]);
    if (monthIndex === null) return null;
    const date = makeUtcDate(expandYear(dmy[3]), monthIndex, Number(dmy[1]));
    return date ? { date, format: { kind: "text", order: "dmy" } } : null;
  }

  const mdy = text.match(TEXT_MDY_RE);
  if (mdy) {
    const monthIndex = monthIndexFromName(mdy[1]);
    if (monthIndex === null) return null;
    const date = makeUtcDate(expandYear(mdy[3]), monthIndex, Number(mdy[2]));
    return date ? { date, format: { kind: "text", order: "mdy" } } : null;
  }

  return null;
}

function pad2(n: number | string): string {
  return String(n).padStart(2, "0");
}

/** Write a date back in the same shape the invoice used. */
export function formatDateLike(date: Date, format?: DateFormat): string {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  const fmt = format || { kind: "iso" as const };

  if (fmt.kind === "numeric") {
    const sep = fmt.sep || "/";
    const year = fmt.yearDigits === 2 ? pad2(y % 100) : String(y);
    if (fmt.order === "ymd") return `${y}${sep}${pad2(m)}${sep}${pad2(d)}`;
    if (fmt.order === "mdy") return `${pad2(m)}${sep}${pad2(d)}${sep}${year}`;
    return `${pad2(d)}${sep}${pad2(m)}${sep}${year}`;
  }

  if (fmt.kind === "text") {
    const label = MONTH_LABELS[date.getUTCMonth()];
    return fmt.order === "mdy" ? `${label} ${d}, ${y}` : `${pad2(d)} ${label} ${y}`;
  }

  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export type ParsedTerms = { days: number; months: number; endOfMonth: boolean; label: string };

/**
 * Read the credit period out of a free-text terms string. null means the string
 * says nothing about timing — which is NOT the same as "due immediately".
 */
export function parsePaymentTerms(raw: unknown): ParsedTerms | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  const t = text.toLowerCase();

  const endOfMonth = /\b(?:e\.?o\.?m\.?|end\s+of\s+(?:the\s+)?month|month\s*end)\b/.test(t);

  // "NET 30", "NET30", "NETT 30" — and the abbreviated "N/30", where the
  // separator is what makes a bare "n" mean "net" rather than a stray letter.
  //
  // "N30", separator and all, is the third arm. It was left out on the theory
  // that a bare letter beside a number is too weak to read as terms, but prod
  // says otherwise: Fineline `0000667597` prints exactly `N30`, and the invoice
  // proves the reading, 08/01/2026 + 30 days = 07/02/2026, its printed due
  // date to the day. Returning null for it meant accounts payable had no due
  // date at all on those invoices (see TECH_DEBT #123). The `\b` after the
  // digits is what keeps this off an order number: "N123456" backtracks to no
  // match, because a word character follows every 1-3 digit prefix, and
  // MAX_TERM_DAYS refuses the rest.
  const net =
    t.match(/\bnett?\s*(\d{1,3})\b/) ||
    t.match(/\bn(?:et)?t?\s*[/\-]\s*(\d{1,3})\b/) ||
    t.match(/\bn(\d{1,3})\b/);
  if (net) {
    const days = Number(net[1]);
    if (days >= 0 && days <= MAX_TERM_DAYS) {
      return { days, months: 0, endOfMonth, label: `net ${days} days` };
    }
  }

  const days = t.match(/\b(\d{1,3})\s*(?:calendar\s*)?d(?:ay|y)s?\b/);
  if (days) {
    const n = Number(days[1]);
    if (n >= 0 && n <= MAX_TERM_DAYS) {
      return { days: n, months: 0, endOfMonth, label: `${n} days` };
    }
  }

  const months = t.match(/\b(\d{1,2})\s*months?\b/);
  if (months) {
    const n = Number(months[1]);
    if (n >= 0 && n <= 12) {
      return { days: 0, months: n, endOfMonth, label: `${n} month${n === 1 ? "" : "s"}` };
    }
  }

  if (
    /\b(?:due\s*(?:up)?on\s*receipt|payable\s*(?:up)?on\s*receipt|on\s*receipt|c\.?o\.?d\.?|cash\s*on\s*delivery|cash|prepaid|pre[\s-]?paid|paid\s*in\s*advance|immediate(?:ly)?)\b/.test(t)
  ) {
    return { days: 0, months: 0, endOfMonth: false, label: "due on receipt" };
  }

  if (endOfMonth) return { days: 0, months: 0, endOfMonth: true, label: "end of month" };

  return null;
}

function lastDayOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}

function addMonths(date: Date, months: number): Date {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const target = new Date(Date.UTC(y, m + months, 1));
  // 31 Jan + 1 month is 28/29 Feb, not 3 March.
  const maxDay = lastDayOfMonth(target).getUTCDate();
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, maxDay)));
}

/**
 * Any date this system holds, as a Date. Null when it is not a date at all.
 *
 * `new Date(string)` cannot read a day-first date — "15/07/26", "23/02/2026"
 * and "21/10/2024" are all Invalid Date to it, and those are the shapes these
 * suppliers print. Anything reading a date off an invoice comes through here.
 */
export function toInvoiceDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // Day-first first: "02/03/2026" is 2 March here, and only the native parser
  // would read it as 3 February.
  const parsed = parseInvoiceDate(value);
  if (parsed) return parsed.date;

  // A day/month numeric date the house parser could not read is not a date,
  // and must NEVER reach the native parser, which is month-first
  // (USC-BUG-0009 / 0012 / 0017 / 0025). See the server twin.
  if (leadsWithDayMonthDate(value)) return null;
  // Nor an ISO date the house parser refused — `new Date` would roll
  // "2027-02-31" into 3 March (USC-BUG-0041). See the server twin.
  if (ISO_RE.test(String(value).trim())) return null;

  // Epoch numbers, a month NAME with a clock, RFC / Date#toString output: no
  // day/month order to get wrong.
  const native = new Date(value as any);
  return Number.isNaN(native.getTime()) ? null : native;
}

/**
 * Does this text START with a numeric date whose first number is a day or a
 * month? Then the house parser's answer is final, whatever follows the date.
 */
export function leadsWithDayMonthDate(raw: unknown): boolean {
  return DAY_MONTH_LEAD_RE.test(String(raw ?? "").trim());
}

/** dd/mm/yyyy — the format this business reads dates in. */
export function formatDdMmYyyy(value: unknown): string {
  const d = toInvoiceDate(value);
  if (!d) return "";
  return formatDateLike(d, { kind: "numeric", sep: "/", order: "dmy", yearDigits: 4 });
}

// A supplier may date an invoice for the delivery day, a day or two ahead.
// Beyond that the date is a misread day, month or year (D34).
export const FUTURE_DATE_TOLERANCE_DAYS = 3;

/**
 * Is this date too far ahead to be a real invoice date? D34. The incident and
 * the clock caveat are in the server twin. False for a date that cannot be
 * read (invariant 8).
 */
export function isFarFutureInvoiceDate(value: unknown, now: unknown = new Date()): boolean {
  const date = toInvoiceDate(value);
  const at = toInvoiceDate(now);
  if (!date || !at) return false;
  return date.getTime() > at.getTime() + FUTURE_DATE_TOLERANCE_DAYS * 86400000;
}

export type InvoiceDateChecks = { future: boolean; notUploadYear: boolean; dueBeforeInvoice: boolean };

/** The three invoice dates worth a second look (D34). Flags, never refusals; see the server twin. */
export function invoiceDateChecks({
  invoiceDate,
  dueDate,
  uploadedAt,
  now = new Date(),
}: { invoiceDate?: unknown; dueDate?: unknown; uploadedAt?: unknown; now?: unknown } = {}): InvoiceDateChecks {
  const issued = toInvoiceDate(invoiceDate);
  const due = toInvoiceDate(dueDate);
  const uploaded = toInvoiceDate(uploadedAt);
  return {
    future: isFarFutureInvoiceDate(issued, now),
    notUploadYear: !!(issued && uploaded) && issued.getUTCFullYear() !== uploaded.getUTCFullYear(),
    dueBeforeInvoice: !!(issued && due) && due.getTime() < issued.getTime(),
  };
}

/**
 * A vendor's credit days as they are ON FILE: whole days above zero, or null
 * when nobody set them. A stored 0 is the schema default, so it reads as "not
 * on file" (NEW-2, invariant 8). The reasoning is in the server twin.
 */
export function creditDaysOnFile(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const days = Number(value);
  return Number.isInteger(days) && days > 0 ? days : null;
}

/**
 * Due date = invoice date + terms. null when either input is unreadable: a
 * guessed due date is worse than an empty one.
 */
export function deriveDueDate(
  invoiceDate: unknown,
  paymentTerms: unknown
): { dueDate: string; note: string } | null {
  const parsedDate = parseInvoiceDate(invoiceDate);
  if (!parsedDate) return null;

  const terms = parsePaymentTerms(paymentTerms);
  if (!terms) return null;

  let due = parsedDate.date;
  if (terms.endOfMonth) due = lastDayOfMonth(due);
  if (terms.months) due = addMonths(due, terms.months);
  if (terms.days) due = new Date(due.getTime() + terms.days * 86400000);

  // Written in the invoice's own shape only while `toInvoiceDate` reads it back as the same day: a
  // month-first "11/10/2026" (10 November) reads day-first as 11 October, before the invoice date
  // (lane 5, V3b). Day-first always survives, so that is the fallback. The reasoning is in the server twin.
  const shaped = formatDateLike(due, parsedDate.format);
  const readsBack = toInvoiceDate(shaped);
  const dueDate = readsBack && readsBack.getTime() === due.getTime() ? shaped : formatDdMmYyyy(due);

  return {
    dueDate,
    note:
      `Calculated from the invoice date (${formatDateLike(parsedDate.date, parsedDate.format)}) ` +
      `and payment terms (${String(paymentTerms).trim()} → ${terms.label}` +
      `${terms.endOfMonth && terms.label !== "end of month" ? " from end of month" : ""}).`,
  };
}
